import React from "react";
import RentalFilters from "./RentalFilters";
import RentalSectionTable from "./RentalSectionTable";
import RentalStatus from "../../enums/RentalStatus";
import ExcelUtil from "../../util/ExcelUtil";
import Roles from "../../enums/Roles";

class RentalTabContent extends React.Component {
  handleExportAllToExcel = () => {
    const {
      rentals,
      selectedMonth,
      selectedYear,
      userRole,
      formatPrice,
      formatStatus,
      calculateMonthlyPrice,
    } = this.props;

    if (!rentals || rentals.length === 0) {
      return;
    }

    const isAdmin = userRole === Roles.ADMIN || userRole === Roles.SALES_REP;

    // Préparer les données pour l'export
    const excelData = rentals.map((rental) => {
      const row = {
        Produit:
          rental.product_name ||
          (rental.product && rental.product.designation) ||
          "Produit non trouvé",
        "Réf. interne":
          rental.ref_interne ||
          (rental.product && rental.product.ref) ||
          "A compléter",
        Demandeur: rental.user
          ? `${rental.user.first_name || ""} ${rental.user.name || ""}`.trim() ||
            "-"
          : "-",
        Début: rental.start_date
          ? new Date(rental.start_date).toLocaleDateString("fr-FR")
          : "N/A",
        Fin: rental.end_date
          ? new Date(rental.end_date).toLocaleDateString("fr-FR")
          : "-",
        Statut: formatStatus(rental.status).text,
        Commentaire: rental.validation_comment || "-",
        "Prix journalier HT":
          rental.daily_price && !isNaN(parseFloat(rental.daily_price))
            ? formatPrice(parseFloat(rental.daily_price))
            : rental.product &&
                rental.product.prix_u_ht_emera != null &&
                !isNaN(parseFloat(rental.product.prix_u_ht_emera))
              ? formatPrice(parseFloat(rental.product.prix_u_ht_emera))
              : "A compléter",
        "Prix de la période mensuel": formatPrice(calculateMonthlyPrice(rental)),
      };

      // Ajouter la colonne Établissement si admin
      if (isAdmin) {
        row["Établissement"] = rental.establishment?.name || "N/A";
      }

      return row;
    });

    // Définir le mapping des colonnes (clés -> en-têtes)
    const columnsMapping = {
      Produit: "PRODUIT",
      "Réf. interne": "RÉF. INTERNE",
      Demandeur: "DEMANDEUR",
      Début: "DÉBUT",
      Fin: "FIN",
      Statut: "STATUT",
      Commentaire: "COMMENTAIRE",
      "Prix journalier HT": "PRIX JOURNALIER HT",
      "Prix de la période mensuel": "PRIX DE LA PRÉRIODE MENSUEL",
    };

    if (isAdmin) {
      columnsMapping["Établissement"] = "ÉTABLISSEMENT";
    }

    // Convertir en format Excel
    const excelArray = ExcelUtil.toExcel(excelData, columnsMapping, []);

    // Générer le nom du fichier
    const monthNames = [
      "janvier",
      "fevrier",
      "mars",
      "avril",
      "mai",
      "juin",
      "juillet",
      "aout",
      "septembre",
      "octobre",
      "novembre",
      "decembre",
    ];
    const today = new Date();
    const dateStr = `${today.getDate()}-${today.getMonth() + 1}-${today.getFullYear()}`;
    const monthName = monthNames[selectedMonth - 1];
    const fileName = `locations_${monthName}_${selectedYear}_${dateStr}`;

    // Sauvegarder le fichier
    ExcelUtil.save(excelArray, fileName);
  };

  render() {
    const {
      rentals,
      selectedMonth,
      selectedYear,
      selectedStatus,
      selectedEstablishment,
      establishments,
      searchTerm,
      searchPlaceholder,
      userRole,
      expandedSections,
      openDropdowns,
      dropdownDirections,
      onMonthChange,
      onYearChange,
      onStatusChange,
      onEstablishmentChange,
      onSearchChange,
      onSyncClick,
      onToggleSection,
      onOpenLightbox,
      onToggleDropdown,
      onCloseAllDropdowns,
      onModifyRental,
      onOpenValidationModal,
      onOpenDeleteModal,
      onOpenExtendModal,
      onOpenTerminateModal,
      onOpenRecoveryModal,
      onCancelEndRequest,
      onOpenResidentModal,
      onOpenInternalRefModal,
      onOpenPriceModal,
      formatPrice,
      formatStatus,
      calculateMonthlyPrice,
      calculateSectionTotal,
      filterRentalsByCategory,
    } = this.props;

    // Generate month names
    const monthNames = [
      "Janvier",
      "Février",
      "Mars",
      "Avril",
      "Mai",
      "Juin",
      "Juillet",
      "Août",
      "Septembre",
      "Octobre",
      "Novembre",
      "Décembre",
    ];

    // Early return showing filters and sync button even when no rentals
    if (!rentals || rentals.length === 0) {
      return (
        <>
          <RentalFilters
            selectedMonth={selectedMonth}
            selectedYear={selectedYear}
            selectedStatus={selectedStatus}
            selectedEstablishment={selectedEstablishment}
            establishments={establishments}
            searchTerm={searchTerm}
            searchPlaceholder={searchPlaceholder}
            userRole={userRole}
            onMonthChange={onMonthChange}
            onYearChange={onYearChange}
            onStatusChange={onStatusChange}
            onEstablishmentChange={onEstablishmentChange}
            onSearchChange={onSearchChange}
            onSyncClick={onSyncClick}
          />
          <div className="alert alert-info" role="alert">
            Aucune location trouvée.
          </div>
        </>
      );
    }

    // Apply search filter (produit ou référence)
    const searchFilteredRentals = searchTerm
      ? rentals.filter((rental) => {
          const searchLower = searchTerm.toLowerCase();
          // Recherche par nom de produit
          const matchesProduct =
            rental.product &&
            rental.product.designation &&
            rental.product.designation.toLowerCase().includes(searchLower);
          // Recherche par référence interne (champ ref_interne de la location)
          const matchesRefInterne =
            rental.ref_interne &&
            rental.ref_interne.toLowerCase().includes(searchLower);
          // Recherche par référence produit (champ product.ref)
          const matchesProductRef =
            rental.product &&
            rental.product.ref &&
            rental.product.ref.toLowerCase().includes(searchLower);
          return matchesProduct || matchesRefInterne || matchesProductRef;
        })
      : rentals;

    // Apply establishment filter
    const establishmentFilteredRentals =
      selectedEstablishment === "all"
        ? searchFilteredRentals
        : searchFilteredRentals.filter(
            (rental) =>
              rental.establishment &&
              rental.establishment._id === selectedEstablishment
          );

    // Apply status filter
    const statusFilteredRentals =
      selectedStatus === "all"
        ? establishmentFilteredRentals
        : establishmentFilteredRentals.filter((rental) => {
            switch (selectedStatus) {
              case "en_attente":
                return rental.status === RentalStatus.EN_ATTENTE;
              case "en_preparation":
                return rental.status === RentalStatus.EN_PREPARATION;
              case "a_livrer":
                return rental.status === RentalStatus.A_LIVRER;
              case "actif":
                return rental.status === RentalStatus.ACTIF;
              case "attente_fin":
                return rental.status === RentalStatus.ATTENTE_FIN;
              case "termine":
                return rental.status === RentalStatus.TERMINE;
              case "date_depassee":
                return rental.status === RentalStatus.DATE_DEPASSEE;
              default:
                return true;
            }
          });

    // Mode recherche : si un terme de recherche est saisi, on affiche tous les résultats sans filtrer par mois
    const isSearchMode = searchTerm && searchTerm.trim() !== "";

    // Apply monthly categorization (seulement si pas en mode recherche)
    const { starts, ongoing, ends, upcoming } = isSearchMode
      ? { starts: [], ongoing: [], ends: [], upcoming: [] }
      : filterRentalsByCategory(statusFilteredRentals);

    // Calculate total for the month (starts + ongoing + ends)
    const totalMonthly = isSearchMode
      ? 0
      : calculateSectionTotal(starts) +
        calculateSectionTotal(ongoing) +
        calculateSectionTotal(ends);

    return (
      <>
        <RentalFilters
          selectedMonth={selectedMonth}
          selectedYear={selectedYear}
          selectedStatus={selectedStatus}
          selectedEstablishment={selectedEstablishment}
          establishments={establishments}
          searchTerm={searchTerm}
          searchPlaceholder={searchPlaceholder}
          userRole={userRole}
          onMonthChange={onMonthChange}
          onYearChange={onYearChange}
          onStatusChange={onStatusChange}
          onEstablishmentChange={onEstablishmentChange}
          onSearchChange={onSearchChange}
          onSyncClick={onSyncClick}
          onExportClick={this.handleExportAllToExcel}
        />

        {/* Section titre et stats - Masqué en mode recherche */}
        {!isSearchMode && (
          <div
            className="d-flex align-items-center justify-content-between mb-4"
            style={{ marginTop: "24px" }}
          >
            <div className="d-flex align-items-center">
              <h4
                className="mb-0"
                style={{
                  fontWeight: "700",
                  color: "#1f2937",
                  fontSize: "1.5rem",
                  letterSpacing: "-0.025em",
                }}
              >
                {monthNames[selectedMonth - 1]} {selectedYear}
              </h4>
              <span
                className="badge"
                style={{
                  marginLeft: "12px",
                  backgroundColor: "#10b981",
                  color: "white",
                  fontSize: "0.95rem",
                  fontWeight: "600",
                  padding: "6px 12px",
                  borderRadius: "6px",
                }}
              >
                Total: {formatPrice(totalMonthly)}
              </span>
            </div>
          </div>
        )}

        {/* Mode recherche : Afficher tous les résultats */}
        {isSearchMode && (
          <div>
            <div
              className="d-flex align-items-center justify-content-between mb-4"
              style={{ marginTop: "24px" }}
            >
              <div className="d-flex align-items-center">
                <h4
                  className="mb-0"
                  style={{
                    fontWeight: "700",
                    color: "#1f2937",
                    fontSize: "1.5rem",
                    letterSpacing: "-0.025em",
                  }}
                >
                  Résultats de recherche
                </h4>
                <span
                  className="badge"
                  style={{
                    marginLeft: "12px",
                    backgroundColor: "#3b82f6",
                    color: "white",
                    fontSize: "0.95rem",
                    fontWeight: "600",
                    padding: "6px 12px",
                    borderRadius: "6px",
                  }}
                >
                  {statusFilteredRentals.length} location(s) trouvée(s)
                </span>
              </div>
            </div>

            {statusFilteredRentals.length > 0 ? (
              <RentalSectionTable
                title={`Résultats pour "${searchTerm}"`}
                rentals={statusFilteredRentals}
                sectionKey="search"
                badgeColor="primary"
                isExpanded={true}
                hideTotalBadge={true}
                sectionTotal={0}
                userRole={userRole}
                openDropdowns={openDropdowns}
                dropdownDirections={dropdownDirections}
                onToggleSection={() => {}}
                onOpenLightbox={onOpenLightbox}
                onToggleDropdown={onToggleDropdown}
                onCloseAllDropdowns={onCloseAllDropdowns}
                onModifyRental={onModifyRental}
                onOpenValidationModal={onOpenValidationModal}
                onOpenDeleteModal={onOpenDeleteModal}
                onOpenExtendModal={onOpenExtendModal}
                onOpenTerminateModal={onOpenTerminateModal}
                onOpenRecoveryModal={onOpenRecoveryModal}
                onCancelEndRequest={onCancelEndRequest}
                onOpenResidentModal={onOpenResidentModal}
                onOpenInternalRefModal={onOpenInternalRefModal}
                onOpenPriceModal={onOpenPriceModal}
                formatPrice={formatPrice}
                formatStatus={formatStatus}
                calculateMonthlyPrice={calculateMonthlyPrice}
              />
            ) : (
              <div className="alert alert-warning" role="alert">
                Aucune location trouvée pour "{searchTerm}".
              </div>
            )}
          </div>
        )}

        {/* Four Sections - Masqué en mode recherche */}
        {!isSearchMode && (
          <div>
            {/* Section 1: Rental Starts */}
            <RentalSectionTable
              title="Débuts de location du mois"
              rentals={starts}
              sectionKey="starts"
              badgeColor="primary"
              isExpanded={expandedSections["starts"]}
              sectionTotal={calculateSectionTotal(starts)}
              userRole={userRole}
              openDropdowns={openDropdowns}
              dropdownDirections={dropdownDirections}
              onToggleSection={onToggleSection}
              onOpenLightbox={onOpenLightbox}
              onToggleDropdown={onToggleDropdown}
              onCloseAllDropdowns={onCloseAllDropdowns}
              onModifyRental={onModifyRental}
              onOpenValidationModal={onOpenValidationModal}
              onOpenDeleteModal={onOpenDeleteModal}
              onOpenExtendModal={onOpenExtendModal}
              onOpenTerminateModal={onOpenTerminateModal}
              onOpenRecoveryModal={onOpenRecoveryModal}
              onCancelEndRequest={onCancelEndRequest}
              onOpenResidentModal={onOpenResidentModal}
              onOpenInternalRefModal={onOpenInternalRefModal}
              onOpenPriceModal={onOpenPriceModal}
              formatPrice={formatPrice}
              formatStatus={formatStatus}
              calculateMonthlyPrice={calculateMonthlyPrice}
            />

            {/* Section 2: Ongoing Rentals */}
            <RentalSectionTable
              title="Locations en cours sur le mois"
              rentals={ongoing}
              sectionKey="ongoing"
              badgeColor="primary"
              isExpanded={expandedSections["ongoing"]}
              sectionTotal={calculateSectionTotal(ongoing)}
              userRole={userRole}
              openDropdowns={openDropdowns}
              dropdownDirections={dropdownDirections}
              onToggleSection={onToggleSection}
              onOpenLightbox={onOpenLightbox}
              onToggleDropdown={onToggleDropdown}
              onCloseAllDropdowns={onCloseAllDropdowns}
              onModifyRental={onModifyRental}
              onOpenValidationModal={onOpenValidationModal}
              onOpenDeleteModal={onOpenDeleteModal}
              onOpenExtendModal={onOpenExtendModal}
              onOpenTerminateModal={onOpenTerminateModal}
              onOpenRecoveryModal={onOpenRecoveryModal}
              onCancelEndRequest={onCancelEndRequest}
              onOpenResidentModal={onOpenResidentModal}
              onOpenInternalRefModal={onOpenInternalRefModal}
              onOpenPriceModal={onOpenPriceModal}
              formatPrice={formatPrice}
              formatStatus={formatStatus}
              calculateMonthlyPrice={calculateMonthlyPrice}
            />

            {/* Section 3: Rental Ends */}
            <RentalSectionTable
              title="Fins de location du mois"
              rentals={ends}
              sectionKey="ends"
              badgeColor="primary"
              isExpanded={expandedSections["ends"]}
              sectionTotal={calculateSectionTotal(ends)}
              userRole={userRole}
              openDropdowns={openDropdowns}
              dropdownDirections={dropdownDirections}
              onToggleSection={onToggleSection}
              onOpenLightbox={onOpenLightbox}
              onToggleDropdown={onToggleDropdown}
              onCloseAllDropdowns={onCloseAllDropdowns}
              onModifyRental={onModifyRental}
              onOpenValidationModal={onOpenValidationModal}
              onOpenDeleteModal={onOpenDeleteModal}
              onOpenExtendModal={onOpenExtendModal}
              onOpenTerminateModal={onOpenTerminateModal}
              onOpenRecoveryModal={onOpenRecoveryModal}
              onCancelEndRequest={onCancelEndRequest}
              onOpenResidentModal={onOpenResidentModal}
              onOpenInternalRefModal={onOpenInternalRefModal}
              onOpenPriceModal={onOpenPriceModal}
              formatPrice={formatPrice}
              formatStatus={formatStatus}
              calculateMonthlyPrice={calculateMonthlyPrice}
            />

            {/* Section 4: Upcoming Rentals */}
            <RentalSectionTable
              title="Locations à venir"
              rentals={upcoming}
              sectionKey="upcoming"
              badgeColor="primary"
              hideTotalBadge={true}
              isExpanded={expandedSections["upcoming"]}
              sectionTotal={calculateSectionTotal(upcoming)}
              userRole={userRole}
              openDropdowns={openDropdowns}
              dropdownDirections={dropdownDirections}
              onToggleSection={onToggleSection}
              onOpenLightbox={onOpenLightbox}
              onToggleDropdown={onToggleDropdown}
              onCloseAllDropdowns={onCloseAllDropdowns}
              onModifyRental={onModifyRental}
              onOpenValidationModal={onOpenValidationModal}
              onOpenDeleteModal={onOpenDeleteModal}
              onOpenExtendModal={onOpenExtendModal}
              onOpenTerminateModal={onOpenTerminateModal}
              onOpenRecoveryModal={onOpenRecoveryModal}
              onCancelEndRequest={onCancelEndRequest}
              onOpenResidentModal={onOpenResidentModal}
              onOpenInternalRefModal={onOpenInternalRefModal}
              onOpenPriceModal={onOpenPriceModal}
              formatPrice={formatPrice}
              formatStatus={formatStatus}
              calculateMonthlyPrice={calculateMonthlyPrice}
            />

            {/* No results message */}
            {(selectedStatus !== "all" || selectedEstablishment !== "all") &&
              starts.length === 0 &&
              ongoing.length === 0 &&
              ends.length === 0 &&
              upcoming.length === 0 && (
                <div className="alert alert-warning" role="alert">
                  Aucune location correspondant à vos critères de recherche pour{" "}
                  {monthNames[selectedMonth - 1]} {selectedYear}.
                </div>
              )}
          </div>
        )}
      </>
    );
  }
}

export default RentalTabContent;
